import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Table } from 'react-bootstrap';
import StageMasterApi from '../../../api/StageMasterApi';
import DataApi from '../../../api/DataApi';
import PicklistApi from '../../../api/PicklistApi';
import EditStageMasterModal from './EditStageMasterModal';
import { extractStageOptions, normalizeHexColor, toStageOption } from './stageMasterUtils';

const StageMasterManager = () => {
  const api = useMemo(() => new StageMasterApi(), []);
  const objectConfigApi = useMemo(() => new DataApi('objectconfig/opportunity'), []);
  const picklistApi = useMemo(() => new PicklistApi(), []);
  const [rows, setRows] = useState([]);
  const [stageOptions, setStageOptions] = useState([]);
  const [formModal, setFormModal] = useState({ open: false, mode: 'create', stage: null });
  const [isSaving, setIsSaving] = useState(false);
  const [feedback, setFeedback] = useState({ text: '', type: null });

  const setSuccess = (text) => setFeedback({ text, type: 'success' });
  const clearFeedback = () => setFeedback({ text: '', type: null });

  const loadData = async () => {
    const response = await api.fetchStages();
    const result = response?.data;
    if (result?.success) {
      setRows(Array.isArray(result.data) ? result.data : []);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const loadStageOptions = async () => {
      try {
        const response = await objectConfigApi.get('');
        const result = response?.data;
        const fields = result?.success ? result?.data?.fields || [] : [];
        const stageField = fields.find(
          (field) =>
            field?.field_name === 'stage' &&
            (field?.data_type === 'picklist' || field?.data_type === 'multipicklist')
        );

        const extracted = extractStageOptions(stageField);
        if (extracted.length > 0) {
          setStageOptions(extracted);
          return;
        }

        if (stageField?.id) {
          const picklistResponse = await picklistApi.getActivePicklistValues(stageField.id);
          const picklistData = picklistResponse?.data?.success ? picklistResponse?.data?.data || [] : [];
          const fallback = picklistData
            .map((pv) =>
              toStageOption(
                pv.value_key || pv.key || pv.value,
                pv.value_label || pv.label || pv.value,
                pv.sort_order
              )
            )
            .filter((opt) => opt.value)
            .sort((a, b) => a.sort_order - b.sort_order || a.label.localeCompare(b.label));
          setStageOptions(fallback);
          return;
        }

        setStageOptions([]);
      } catch (error) {
        console.error('Failed to load opportunity stage options:', error);
        setStageOptions([]);
      }
    };

    loadStageOptions();
  }, [objectConfigApi, picklistApi]);

  const closeFormModal = () => {
    setFormModal({ open: false, mode: 'create', stage: null });
  };

  const openCreate = () => {
    clearFeedback();
    setFormModal({ open: true, mode: 'create', stage: null });
  };

  const openEdit = (item) => {
    clearFeedback();
    setFormModal({ open: true, mode: 'edit', stage: item });
  };

  const handleSave = async ({ payload, editingId }) => {
    setIsSaving(true);
    try {
      const response = editingId
        ? await api.updateStage(editingId, payload)
        : await api.createStage(payload);
      const result = response?.data;
      if (result?.success) {
        setSuccess(editingId ? 'Stage updated successfully.' : 'Stage created successfully.');
        closeFormModal();
        await loadData();
      } else {
        throw new Error(result?.message || 'Failed to save stage.');
      }
    } catch (error) {
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const onDelete = async (id) => {
    clearFeedback();
    setIsSaving(true);
    try {
      const response = await api.deleteStage(id);
      const result = response?.data;
      if (result?.success) {
        if (formModal.stage?.id === id) {
          closeFormModal();
        }
        setSuccess('Stage deleted successfully.');
        await loadData();
      } else {
        setFeedback({ text: result?.message || 'Failed to delete stage.', type: 'error' });
      }
    } catch (error) {
      setFeedback({
        text: error?.response?.data?.message || error?.message || 'Failed to delete stage.',
        type: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const renderBooleanBadge = (value, trueLabel, falseLabel) => (
    <Badge bg={value ? 'success' : 'secondary'}>{value ? trueLabel : falseLabel}</Badge>
  );

  return (
    <div className="wrapper">
      <div id="content">
        <Card className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h4 className="mb-1">Probability Manager</h4>
              <p className="text-muted mb-0">
                Manage stage probability, forecast category, and closed/won flags for your opportunity pipeline.
              </p>
            </div>
            <Button variant="primary" onClick={openCreate} disabled={isSaving}>
              Add Stage
            </Button>
          </div>

          {feedback.text ? (
            <div
              className={
                feedback.type === 'success' ? 'mb-3 text-success' : 'mb-3 text-danger'
              }
              role={feedback.type === 'success' ? 'status' : 'alert'}
            >
              {feedback.text}
            </div>
          ) : null}

          <Table bordered hover responsive>
            <thead>
              <tr>
                <th>Stage</th>
                <th>Probability</th>
                <th>Sequence</th>
                <th>Forecast Category</th>
                <th>Closed</th>
                <th>Won</th>
                <th>Color</th>
                <th style={{ width: 170 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-muted text-center py-4">
                    No stages found. Click &quot;Add Stage&quot; to create one.
                  </td>
                </tr>
              ) : (
                rows.map((item) => (
                  <tr key={item.id}>
                    <td>{item.name}</td>
                    <td>{Number(item.default_probability).toFixed(2)}%</td>
                    <td>{item.sequence_order}</td>
                    <td>{item.forecast_category || 'Pipeline'}</td>
                    <td>{renderBooleanBadge(item.is_closed, 'Yes', 'No')}</td>
                    <td>{renderBooleanBadge(item.is_won, 'Yes', 'No')}</td>
                    <td>
                      <span
                        className="d-inline-block rounded border me-2"
                        style={{
                          width: 18,
                          height: 18,
                          verticalAlign: 'middle',
                          backgroundColor: normalizeHexColor(item.color),
                        }}
                      />
                      {normalizeHexColor(item.color)}
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button size="sm" variant="outline-primary" onClick={() => openEdit(item)} disabled={isSaving}>
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => onDelete(item.id)}
                          disabled={isSaving}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card>

        <EditStageMasterModal
          show={formModal.open}
          onHide={closeFormModal}
          mode={formModal.mode}
          stage={formModal.stage}
          stageOptions={stageOptions}
          onSave={handleSave}
          saving={isSaving}
        />
      </div>
    </div>
  );
};

export default StageMasterManager;
